import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Table,
  Button,
  Modal,
  Spinner,
  Alert,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import moment from "moment";
import SideNavbar from "./helpers/SideNavbar";
import { FaTimes, FaRedo, FaInfoCircle } from 'react-icons/fa';
import {
  useFetchPaymentsByUser,
  useHandleSubscription,
} from "../apis/payment";

const hasSubscriptionId = (id) => /sub_\w+/.test(id);

const PaymentManager = () => {
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [confirmation, setConfirmation] = useState({
    show: false,
    action: "",
    subscriptionId: "",
  });

  const userId = localStorage.getItem("UserID");
  const {
    data: payments,
    isLoading: loading,
    isError: error,
    refetch,
  } = useFetchPaymentsByUser(userId);

  const {
    mutate: handleSubscription,
    isLoading: mutationLoading,
  } = useHandleSubscription();

  const openConfirmation = (action, subscriptionId) => {
    setConfirmation({ show: true, action, subscriptionId });
  };

  const handleConfirmAction = () => {
    const { action, subscriptionId } = confirmation;
    handleSubscription(
      { subscriptionId, cancelAtPeriodEnd: action === "cancel" },
      {
        onSuccess: () => {
          refetch();
          closeConfirmation();
        },
      }
    );
  };

  const closeConfirmation = () => {
    setConfirmation({ show: false, action: "", subscriptionId: "" });
  };

  const openDetails = (payment) => {
    setSelectedPayment(payment);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedPayment(null);
    setShowModal(false);
  };

  return (
    <SideNavbar>
      <Container fluid className="px-2 px-md-3 py-2 py-md-4" style={{ maxWidth: '100%', overflowX: 'hidden' }}>
        <div className="mb-3">
          <Link to="/subscription">
            <h5 className="welcome-text" style={{ fontSize: 'clamp(16px, 4vw, 20px)', color: '#2e3192', textAlign: 'left' }}>
              <i className="fa fa-arrow-left d-none d-md-inline" style={{ marginRight: '30px' }} aria-hidden="true"></i>
              <i className="fa fa-arrow-left d-md-none" style={{ marginRight: '10px' }} aria-hidden="true"></i>
              Payment History
            </h5>
          </Link>
        </div>

        {loading && <Spinner animation="border" />}
        {error && <Alert variant="danger">Error fetching payment data</Alert>}

        {!loading && payments.length === 0 && (
          <Alert variant="info">No payment records found.</Alert>
        )}

        {!loading && payments.length > 0 && (
          <div className="card shadow-sm" style={{ border: 'none', borderRadius: '12px', overflow: 'hidden' }}>
            <div className="card-body p-0">
              {/* Desktop Table View */}
              <div className="table-responsive d-none d-md-block">
                <Table 
                  striped 
                  bordered 
                  hover 
                  style={{ 
                    marginBottom: 0,
                    fontSize: '14px'
                  }}
                >
                  <thead style={{ backgroundColor: "#2e3192", color: "white" }}>
                    <tr>
                      <th style={{ padding: '12px', fontWeight: '600', fontSize: '14px' }}>Transaction ID</th>
                      <th style={{ padding: '12px', fontWeight: '600', fontSize: '14px' }}>Type</th>
                      <th style={{ padding: '12px', fontWeight: '600', fontSize: '14px' }}>Status</th>
                      <th style={{ padding: '12px', fontWeight: '600', fontSize: '14px' }}>Amount</th>
                      <th style={{ padding: '12px', fontWeight: '600', fontSize: '14px' }}>Payment Date</th>
                      <th style={{ padding: '12px', fontWeight: '600', fontSize: '14px' }}>Next Billing</th>
                      <th style={{ padding: '12px', fontWeight: '600', fontSize: '14px', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.Stripepaymentid} style={{ verticalAlign: 'middle' }}>
                        <td style={{ padding: '12px', fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' }}>
                          {payment.Stripepaymentid ? 
                            (payment.Stripepaymentid.length > 20 
                              ? `${payment.Stripepaymentid.substring(0, 20)}...` 
                              : payment.Stripepaymentid)
                            : 'N/A'}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <Badge 
                            bg={hasSubscriptionId(payment.Stripepaymentid) ? "primary" : "secondary"}
                            style={{ fontSize: '12px', padding: '6px 10px' }}
                          >
                            {hasSubscriptionId(payment.Stripepaymentid) ? "Subscription" : "One-time"}
                          </Badge>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <Badge
                            bg={
                              payment.Payment_status === "active" || payment.Payment_status === "succeeded"
                                ? "success"
                                : payment.Payment_status === "past_due"
                                ? "warning"
                                : "danger"
                            }
                            style={{ fontSize: '12px', padding: '6px 10px', marginRight: '6px' }}
                          >
                            {payment.Payment_status}
                          </Badge>
                          {hasSubscriptionId(payment.Stripepaymentid) && (
                            <Badge
                              bg={payment.AutoRenew || (!payment.cancel_at_period_end) ? "info" : "warning"}
                              style={{ fontSize: '11px', padding: '4px 8px' }}
                            >
                              {payment.AutoRenew || (!payment.cancel_at_period_end) ? "Auto-Renew ON" : "Auto-Renew OFF"}
                            </Badge>
                          )}
                        </td>
                        <td style={{ padding: '12px', fontWeight: '600', color: '#2e3192' }}>
                          ${payment.Amount} {payment.Currency ? payment.Currency.toUpperCase() : 'USD'}
                        </td>
                        <td style={{ padding: '12px', color: '#374151' }}>
                          {moment(payment.CreatedDate).format("MMM DD, YYYY")}
                          <br />
                          <small style={{ color: '#9ca3af', fontSize: '11px' }}>
                            {moment(payment.CreatedDate).format("hh:mm A")}
                          </small>
                        </td>
                        <td style={{ padding: '12px', color: '#374151' }}>
                          {hasSubscriptionId(payment.Stripepaymentid)
                            ? (
                              <>
                                {moment(payment.EndDate).format("MMM DD, YYYY")}
                                <br />
                                <small style={{ color: '#9ca3af', fontSize: '11px' }}>
                                  {moment(payment.EndDate).format("hh:mm A")}
                                </small>
                              </>
                            )
                            : <span style={{ color: '#9ca3af' }}>N/A</span>}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <div className="d-flex flex-wrap gap-2 align-items-center justify-content-center">
                            {hasSubscriptionId(payment.Stripepaymentid) && !payment.cancel_at_period_end && (
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="cancel-tooltip">Cancel Subscription</Tooltip>}
                              >
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => openConfirmation("cancel", payment.Stripepaymentid)}
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '8px'
                                  }}
                                >
                                  <FaTimes style={{ fontSize: '16px' }} />
                                </Button>
                              </OverlayTrigger>
                            )}
                            {hasSubscriptionId(payment.Stripepaymentid) && payment.cancel_at_period_end && (
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="renew-tooltip">Renew Subscription</Tooltip>}
                              >
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => openConfirmation("renew", payment.Stripepaymentid)}
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '8px'
                                  }}
                                >
                                  <FaRedo style={{ fontSize: '16px' }} />
                                </Button>
                              </OverlayTrigger>
                            )}
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id="details-tooltip">View Payment Details</Tooltip>}
                            >
                              <Button
                                style={{ 
                                  backgroundColor: "#2e3192",
                                  width: '36px',
                                  height: '36px',
                                  padding: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '8px',
                                  border: 'none'
                                }}
                                size="sm"
                                onClick={() => openDetails(payment)}
                              >
                                <FaInfoCircle style={{ fontSize: '16px', color: '#fff' }} />
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="d-md-none">
                {payments.map((payment) => (
                  <div 
                    key={payment.Stripepaymentid}
                    className="card mb-3 mx-2"
                    style={{ 
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      marginTop: '12px'
                    }}
                  >
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <Badge 
                            bg={hasSubscriptionId(payment.Stripepaymentid) ? "primary" : "secondary"}
                            style={{ fontSize: '11px', padding: '4px 8px', marginBottom: '4px' }}
                          >
                            {hasSubscriptionId(payment.Stripepaymentid) ? "Subscription" : "One-time"}
                          </Badge>
                          <div style={{ fontSize: '10px', color: '#6b7280', fontFamily: 'monospace', marginTop: '4px' }}>
                            {payment.Stripepaymentid ? 
                              (payment.Stripepaymentid.length > 15 
                                ? `${payment.Stripepaymentid.substring(0, 15)}...` 
                                : payment.Stripepaymentid)
                              : 'N/A'}
                          </div>
                        </div>
                        <div className="text-end">
                          <div style={{ fontWeight: '700', color: '#2e3192', fontSize: '16px' }}>
                            ${payment.Amount}
                          </div>
                          <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                            {payment.Currency ? payment.Currency.toUpperCase() : 'USD'}
                          </div>
                        </div>
                      </div>

                      <div className="mb-2">
                        <Badge
                          bg={
                            payment.Payment_status === "active" || payment.Payment_status === "succeeded"
                              ? "success"
                              : payment.Payment_status === "past_due"
                              ? "warning"
                              : "danger"
                          }
                          style={{ fontSize: '11px', padding: '4px 8px', marginRight: '6px' }}
                        >
                          {payment.Payment_status}
                        </Badge>
                        {hasSubscriptionId(payment.Stripepaymentid) && (
                          <Badge
                            bg={payment.AutoRenew || (!payment.cancel_at_period_end) ? "info" : "warning"}
                            style={{ fontSize: '10px', padding: '4px 6px' }}
                          >
                            {payment.AutoRenew || (!payment.cancel_at_period_end) ? "Auto-Renew ON" : "Auto-Renew OFF"}
                          </Badge>
                        )}
                      </div>

                      <div className="row mb-2" style={{ fontSize: '12px', color: '#374151' }}>
                        <div className="col-6">
                          <div style={{ color: '#9ca3af', fontSize: '11px' }}>Payment Date</div>
                          <div>{moment(payment.CreatedDate).format("MMM DD, YYYY")}</div>
                          <div style={{ color: '#9ca3af', fontSize: '10px' }}>
                            {moment(payment.CreatedDate).format("hh:mm A")}
                          </div>
                        </div>
                        <div className="col-6">
                          <div style={{ color: '#9ca3af', fontSize: '11px' }}>Next Billing</div>
                          {hasSubscriptionId(payment.Stripepaymentid) ? (
                            <>
                              <div>{moment(payment.EndDate).format("MMM DD, YYYY")}</div>
                              <div style={{ color: '#9ca3af', fontSize: '10px' }}>
                                {moment(payment.EndDate).format("hh:mm A")}
                              </div>
                            </>
                          ) : (
                            <span style={{ color: '#9ca3af' }}>N/A</span>
                          )}
                        </div>
                      </div>

                      <div className="d-flex gap-2 justify-content-end mt-3">
                        {hasSubscriptionId(payment.Stripepaymentid) && !payment.cancel_at_period_end && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="cancel-tooltip-mobile">Cancel Subscription</Tooltip>}
                          >
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => openConfirmation("cancel", payment.Stripepaymentid)}
                              style={{
                                width: '32px',
                                height: '32px',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '6px'
                              }}
                            >
                              <FaTimes style={{ fontSize: '14px' }} />
                            </Button>
                          </OverlayTrigger>
                        )}
                        {hasSubscriptionId(payment.Stripepaymentid) && payment.cancel_at_period_end && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="renew-tooltip-mobile">Renew Subscription</Tooltip>}
                          >
                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => openConfirmation("renew", payment.Stripepaymentid)}
                              style={{
                                width: '32px',
                                height: '32px',
                                padding: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '6px'
                              }}
                            >
                              <FaRedo style={{ fontSize: '14px' }} />
                            </Button>
                          </OverlayTrigger>
                        )}
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id="details-tooltip-mobile">View Payment Details</Tooltip>}
                        >
                          <Button
                            style={{ 
                              backgroundColor: "#2e3192",
                              width: '32px',
                              height: '32px',
                              padding: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: '6px',
                              border: 'none'
                            }}
                            size="sm"
                            onClick={() => openDetails(payment)}
                          >
                            <FaInfoCircle style={{ fontSize: '14px', color: '#fff' }} />
                          </Button>
                        </OverlayTrigger>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Payment Details Modal */}
        <Modal show={showModal} onHide={closeModal} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: 'clamp(16px, 4vw, 20px)' }}>Payment Details</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ fontSize: '14px' }}>
            {selectedPayment ? (
              <>
                <p><strong>Stripe Payment ID:</strong> {selectedPayment.Stripepaymentid}</p>
                <p><strong>Type:</strong> {hasSubscriptionId(selectedPayment.Stripepaymentid) ? "Subscription" : "One-time"}</p>
                <p><strong>Full Name:</strong> {selectedPayment.FullName}</p>
                <p><strong>Email:</strong> {selectedPayment.Email}</p>
                <p><strong>Status:</strong> {selectedPayment.Payment_status}</p>
                <p><strong>Amount:</strong> ${selectedPayment.Amount} {selectedPayment.Currency?.toUpperCase()}</p>
                <p><strong>Card:</strong> {selectedPayment.Card_brand} ending in {selectedPayment.Card_last4}</p>
                <p><strong>Receipt:</strong> {(() => {
                  const id = selectedPayment.Stripepaymentid || '';
                  const isPaymentIntent = String(id).startsWith('pi_');
                  const fallback = selectedPayment.Receipt_url ? `&receiptUrl=${encodeURIComponent(selectedPayment.Receipt_url)}` : '';
                  const onsiteUrl = isPaymentIntent ? `/receipt?paymentIntentId=${id}${fallback}` : null;
                  if (onsiteUrl) {
                    return (
                      <Link to={onsiteUrl} className="btn btn-link">
                        View Receipt
                      </Link>
                    );
                  }
                  return (
                    <a href={selectedPayment.Receipt_url} target="_blank" rel="noopener noreferrer" className="btn btn-link">
                      View Receipt
                    </a>
                  );
                })()}</p>
                {/* <p><strong>Created Date:</strong> {moment(selectedPayment.CreatedDate).format("LLLL")}</p> */}
                {/* <p><strong>Start Date:</strong> {moment(selectedPayment.StartDate).format("LLLL")}</p> */}
                <p><strong>End Date:</strong> {moment(selectedPayment.EndDate).format("LLLL")}</p>
                <p><strong>Duration Type:</strong> {selectedPayment.SubscriptionType || "N/A"}</p>
                {hasSubscriptionId(selectedPayment.Stripepaymentid) && (
                  <p>
                    <strong>Auto-Renew:</strong>{" "}
                    <Badge bg={selectedPayment.AutoRenew || (!selectedPayment.cancel_at_period_end) ? "success" : "warning"}>
                      {selectedPayment.AutoRenew || (!selectedPayment.cancel_at_period_end) ? "Enabled" : "Disabled"}
                    </Badge>
                  </p>
                )}
              </>
            ) : (
              <Spinner animation="border" />
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>Close</Button>
          </Modal.Footer>
        </Modal>

        {/* Confirmation Modal */}
        <Modal show={confirmation.show} onHide={closeConfirmation} centered>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: 'clamp(16px, 4vw, 20px)' }}>
              {confirmation.action === "cancel" ? "Cancel Subscription" : "Renew Subscription"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ fontSize: '14px' }}>
            Are you sure you want to {" "}
            <strong>{confirmation.action === "cancel" ? "cancel" : "renew"}</strong> this subscription?
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeConfirmation}>No</Button>
            <Button
              variant={confirmation.action === "cancel" ? "danger" : "success"}
              onClick={handleConfirmAction}
              disabled={mutationLoading}
            >
              {mutationLoading ? "Processing..." : "Yes"}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </SideNavbar>
  );
};

export default PaymentManager;
